/**
 * Title Searches Hook
 *
 * React Query hook for fetching and managing title search data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  titleSearchesApi,
  type TitleSearchRequest,
  type LienVerificationRequest,
  type TitleSearchStatus,
} from '../api/client';

const QUERY_KEY = 'titleSearches';

interface UseTitleSearchesParams {
  status?: TitleSearchStatus;
  tenantId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Hook for listing title searches
 */
export function useTitleSearches(params?: UseTitleSearchesParams) {
  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => titleSearchesApi.list(params),
  });
}

/**
 * Hook for fetching a single title search
 */
export function useTitleSearch(searchId: string) {
  return useQuery({
    queryKey: [QUERY_KEY, searchId],
    queryFn: () => titleSearchesApi.get(searchId),
    enabled: !!searchId,
  });
}

/**
 * Hook for initiating a new title search
 */
export function useCreateTitleSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: TitleSearchRequest) =>
      titleSearchesApi.create(request),
    onSuccess: () => {
      // Invalidate the searches list to refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Hook for verifying lien position
 */
export function useVerifyLien() {
  return useMutation({
    mutationFn: (request: LienVerificationRequest) =>
      titleSearchesApi.verifyLien(request),
  });
}
